import { useMemo } from "react";
import { Users, Fuel, Cog, Snowflake, Gauge, ShoppingBag } from "lucide-react";
import VehicleCarousel from "./VehicleCarousel";
import { fmtEUR, prettyFuel, prettyTransmission } from "./formatters";

function Tag({ icon, children }) {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-black/10 bg-neutral-50">
      {icon} {children}
    </span>
  );
}

export default function VehicleCard({ vehicle, days, onBook }) {
  const images = useMemo(() => {
    const seen = new Set();
    const list = [];
    if (vehicle.image_url) { list.push(vehicle.image_url); seen.add(vehicle.image_url); }
    for (const g of (vehicle.gallery || [])) {
      if (g && !seen.has(g)) { list.push(g); seen.add(g); }
    }
    return list;
  }, [vehicle.image_url, vehicle.gallery]);
  const available = vehicle.available_for_dates !== false;
  const uponRequest = vehicle.status === "upon_request";
  const dailyRate = Number(vehicle.daily_rate || 0);
  const total = days > 0 ? Math.round(dailyRate * days) : dailyRate;
  const supportsAirport = vehicle.pickup_locations
    ? vehicle.pickup_locations.some((l) => (l || "").toLowerCase().includes("airport"))
    : true;
  const supportsPort = vehicle.pickup_locations
    ? vehicle.pickup_locations.some((l) => (l || "").toLowerCase().includes("port"))
    : true;

  return (
    <article
      className={`bg-white border border-black/10 rounded-lg overflow-hidden transition-transform hover:-translate-y-0.5 flex flex-col ${available ? "" : "opacity-60"}`}
      data-testid={`pb-vehicle-${vehicle.id}`}
    >
      <VehicleCarousel images={images} alt={vehicle.name} vehicleId={vehicle.id} />
      {!available && (
        <span className="absolute top-2 left-2 text-[10px] px-2 py-0.5 bg-black/70 text-white rounded-full">Unavailable</span>
      )}
      <div className="p-4 flex flex-col gap-3 flex-1">
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">
            {vehicle.category || "—"} · {vehicle.year || ""}
          </p>
          {vehicle.group_code && (
            <span
              className="inline-block mt-1 text-[10px] font-medium tracking-wide uppercase px-2 py-0.5 rounded bg-indigo-100 text-indigo-800"
              data-testid={`pb-group-code-${vehicle.id}`}
            >
              {vehicle.group_code}
            </span>
          )}
          <h3 className="font-display text-lg leading-tight mt-1">
            {vehicle.name} <span className="text-neutral-400 text-sm">or similar</span>
          </h3>
        </div>
        <div className="flex flex-wrap gap-1.5 text-[11px] text-neutral-600">
          <Tag icon={<Users size={11} />}>{vehicle.seats || 5} seats</Tag>
          <Tag icon={<ShoppingBag size={11} />}>{vehicle.bags || 2} bags</Tag>
          <Tag icon={<Cog size={11} />}>{prettyTransmission(vehicle.transmission)}</Tag>
          <Tag icon={<Snowflake size={11} />}>A/C</Tag>
          <Tag icon={<Gauge size={11} />}>Unlimited km</Tag>
          {vehicle.fuel && <Tag icon={<Fuel size={11} />}>{prettyFuel(vehicle.fuel)}</Tag>}
        </div>
        <div className="flex gap-1.5 text-[10px]">
          {supportsAirport && <span className="px-2 py-0.5 rounded-full border border-emerald-300 bg-emerald-50 text-emerald-800">Airport pickup</span>}
          {supportsPort && <span className="px-2 py-0.5 rounded-full border border-sky-300 bg-sky-50 text-sky-800">Port pickup</span>}
        </div>
        <div className="mt-auto flex items-end justify-between pt-3 border-t border-black/8">
          <div>
            {uponRequest ? (
              <div data-testid={`pb-upon-request-badge-${vehicle.id}`}>
                <p className="text-[10px] uppercase tracking-[0.2em] text-amber-700">Upon request</p>
                <p className="text-lg font-semibold text-amber-800 leading-tight">Upon Request</p>
              </div>
            ) : (
              <>
                <p className="tabular-nums leading-none">
                  <span className="text-3xl font-bold tracking-tight text-neutral-900">{fmtEUR(dailyRate)}</span>
                  <span className="ml-1 text-sm font-medium text-neutral-500">/day</span>
                </p>
                {days > 0 && (
                  <p className="text-[11px] text-neutral-500 mt-1.5" data-testid={`pb-total-${vehicle.id}`}>
                    Total {fmtEUR(total)} · {days} {days === 1 ? "day" : "days"}
                  </p>
                )}
              </>
            )}
          </div>
          <button
            type="button"
            onClick={onBook}
            disabled={!uponRequest && !available}
            className={`text-xs font-medium px-4 py-2 rounded ${
              uponRequest
                ? "bg-amber-500 text-white hover:bg-amber-600"
                : "bg-black text-white hover:bg-neutral-800 disabled:opacity-40"
            }`}
            data-testid={`pb-book-${vehicle.id}`}
          >
            {uponRequest ? "Inquire" : "Book"}
          </button>
        </div>
      </div>
    </article>
  );
}
